import { Warehouse } from 'models';
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

type WarehouseContextType = {
  warehouses: Warehouse[];
  activeWarehouse?: Warehouse;
  setWarehouses: (data: Warehouse[]) => void;
  setActiveWarehouse: (warehouseId: string) => void;
  clearWarehouses: () => void;
};

const WarehouseContext = createContext<WarehouseContextType | undefined>(undefined);

export const useWarehouseContext = () => {
  const ctx = useContext(WarehouseContext);
  if (!ctx) {
    throw new Error('useWarehouseContext must be used within WarehouseProvider');
  }
  return ctx;
};

export const WarehouseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [warehouses, setWarehousesState] = useState<Warehouse[]>([]);
  const [activeWarehouseId, setActiveWarehouseId] = useState<string | undefined>();

  const setWarehouses = useCallback(
    (data: Warehouse[]) => {
      setWarehousesState(data);

      // auto select first active warehouse
      if (!activeWarehouseId) {
        const firstActive = data.find((w) => w.isActive);
        if (firstActive) {
          setActiveWarehouseId(firstActive.id);
        }
      }
    },
    [activeWarehouseId],
  );

  const setActiveWarehouse = useCallback((warehouseId: string) => {
    setActiveWarehouseId(warehouseId);
  }, []);

  const clearWarehouses = useCallback(() => {
    setWarehousesState([]);
    setActiveWarehouseId(undefined);
  }, []);

  const activeWarehouse = warehouses.find((w) => w.id === activeWarehouseId);

  return (
    <WarehouseContext.Provider
      value={{
        warehouses,
        activeWarehouse,
        setWarehouses,
        setActiveWarehouse,
        clearWarehouses,
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
};
